import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface BrandingAsset {
  id: string;
  setting_key: 'logo' | 'favicon';
  file_path: string | null;
  file_url: string | null;
  file_name: string | null;
  content_type: string | null;
  size: number | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface BrandingUploadResult {
  fileUrl: string;
  filePath: string;
  fileName: string;
  contentType: string;
  size: number;
}

const BUCKET = 'privatefiles';
const FOLDER = 'branding';
const SIGNED_URL_TTL = 60 * 60 * 24 * 365; // 1 year

export const useBranding = () => {
  const [branding, setBranding] = useState<Record<string, BrandingAsset>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBranding = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error: supaError } = await supabase
        .from('branding_settings')
        .select('*')
        .order('setting_key', { ascending: true });

      if (supaError) throw supaError;

      const map: Record<string, BrandingAsset> = {};
      data?.forEach((item) => {
        map[item.setting_key] = item as BrandingAsset;
      });
      setBranding(map);
    } catch (err: any) {
      setError(err?.message || 'Failed to load branding');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBranding();
  }, [fetchBranding]);

  const uploadBrandingFile = async (
    key: 'logo' | 'favicon',
    file: File
  ): Promise<BrandingUploadResult> => {
    const ext = file.name.split('.').pop() || '';
    const safeName = `${Date.now()}-${Math.random().toString(36).substring(2, 10)}.${ext}`;
    const filePath = `${FOLDER}/${key}/${safeName}`;

    const { error: uploadError } = await supabase.storage
      .from(BUCKET)
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true,
        contentType: file.type,
      });

    if (uploadError) {
      throw new Error(uploadError.message);
    }

    const { data: signedUrlData, error: signedUrlError } = await supabase.storage
      .from(BUCKET)
      .createSignedUrl(filePath, SIGNED_URL_TTL);

    if (signedUrlError || !signedUrlData?.signedUrl) {
      throw new Error(signedUrlError?.message || 'Failed to generate file URL');
    }

    const { error: upsertError } = await supabase
      .from('branding_settings')
      .upsert(
        {
          setting_key: key,
          file_path: filePath,
          file_url: signedUrlData.signedUrl,
          file_name: file.name,
          content_type: file.type,
          size: file.size,
        },
        { onConflict: 'setting_key' }
      );

    if (upsertError) {
      throw new Error(upsertError.message);
    }

    await fetchBranding();

    return {
      fileUrl: signedUrlData.signedUrl,
      filePath,
      fileName: file.name,
      contentType: file.type,
      size: file.size,
    };
  };

  const resetToDefault = async (key: 'logo' | 'favicon') => {
    const defaults: Record<string, Partial<BrandingAsset>> = {
      logo: {
        file_path: null,
        file_url: '/assets/referme-logo.png',
        file_name: 'referme-logo.png',
        content_type: 'image/png',
        size: 951204,
      },
      favicon: {
        file_path: null,
        file_url: '/favicon.png',
        file_name: 'referme-favicon.png',
        content_type: 'image/png',
        size: 2150296,
      },
    };

    const { error: upsertError } = await supabase
      .from('branding_settings')
      .upsert(
        {
          setting_key: key,
          ...defaults[key],
        },
        { onConflict: 'setting_key' }
      );

    if (upsertError) {
      throw new Error(upsertError.message);
    }

    await fetchBranding();
  };

  return {
    branding,
    logo: branding.logo,
    favicon: branding.favicon,
    loading,
    error,
    refresh: fetchBranding,
    uploadBrandingFile,
    resetToDefault,
  };
};

export const useDynamicFavicon = () => {
  const { favicon } = useBranding();

  useEffect(() => {
    if (!favicon?.file_url) return;

    const existing = document.querySelector("link[rel='icon']") as HTMLLinkElement | null;
    const link = existing || document.createElement('link');
    link.rel = 'icon';
    link.type = favicon.content_type || 'image/png';
    link.href = favicon.file_url;

    if (!existing) {
      document.head.appendChild(link);
    }

    return () => {
      if (link.parentNode) {
        link.parentNode.removeChild(link);
      }
    };
  }, [favicon?.file_url, favicon?.content_type]);
};
